import React, { useState, useRef, useEffect, CSSProperties } from 'react';
import { ChevronDown, ChevronUp, Bot, Power, CheckCircle, Clock, Plus, Settings, ArrowRight, X, Info } from 'lucide-react';
import { botSelectByQuery, DBBotThunks, DBBotDeploymentThunks } from '@common-reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Bot as BotType } from '@common/models/bot';
import { toggleBotActivationThunk, createAndReRouteToNewBotEditorThunk } from '@common/reducers/ChatbotThunks';
import { setIsReadyToLoadFlow } from '@common/reducers/ChatbotReducer';
import { useRouter } from '@common-reducers/hooks/use-router';
import ExpandedView from '@common/components/expanded-view';
import { selectIsDarkMode } from '@common-reducers/UserSelectors';

// Define styles similar to connected-engines component
const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  minifiedButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '8px 12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    cursor: 'pointer',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  statusLabel: {
    fontSize: '14px',
    fontWeight: 500,
  },
  expandedView: {
    position: 'absolute',
    top: '100%',
    right: '0',
    marginTop: '8px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '16px',
    minWidth: '380px',
    zIndex: 1000,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: {
    fontWeight: 500,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: '#6b7280',
  },
  // Additional styles for the content
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 0',
  },
  loadingSpinner: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid #e5e7eb',
    borderTopColor: '#22c55e',
    animation: 'spin 1s linear infinite',
    marginBottom: '8px',
  },
  loadingText: {
    fontSize: '14px',
    color: '#6b7280',
  },
  emptyStateContainer: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  emptyStateIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#f3f4f6',
    marginBottom: '12px',
  },
  emptyStateText: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '16px',
    maxWidth: '280px',
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  botItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
  },
  botItemHover: {
    backgroundColor: '#f9fafb',
  },
  botItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  botInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  botName: {
    fontSize: '14px',
    fontWeight: 500,
    margin: 0,
  },
  botMeta: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: '#6b7280',
  },
  toggleContainer: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  toggleTrack: {
    width: '36px',
    height: '20px',
    backgroundColor: '#e5e7eb',
    borderRadius: '10px',
    position: 'relative',
  },
  toggleTrackActive: {
    backgroundColor: '#22c55e',
  },
  toggleThumb: {
    position: 'absolute',
    width: '16px',
    height: '16px',
    backgroundColor: 'white',
    borderRadius: '50%',
    top: '2px',
    left: '2px',
    transition: 'transform 0.2s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  toggleThumbActive: {
    transform: 'translateX(16px)',
  },
  spinner: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #e5e7eb',
    borderTopColor: '#22c55e',
    animation: 'spin 1s linear infinite',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderTop: '1px solid #f3f4f6',
  },
  footerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: '0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  createBotButton: {
    color: '#22c55e',
    fontWeight: 500,
  },
  manageBotButton: {
    color: '#6b7280',
    fontWeight: 500,
  },
  engineItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
};

const BotStatusComponent = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [togglingBots, setTogglingBots] = useState<Set<string>>(new Set());
  const containerRef = useRef(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const instanceId = React.useRef(`bot-status-${Math.random().toString(36).substr(2, 9)}`).current;
  const isDarkMode = useAppSelector(selectIsDarkMode);
  
  // Get bots from the Redux store
  const bots: BotType[] = useAppSelector((state) => botSelectByQuery(state.DBBotReducer, {}));
  const isLoading = useAppSelector(state => state.DBBotReducer.itemsLoading);
  
  // Load bots data when component mounts
  useEffect(() => {
    dispatch(DBBotThunks.find({ $paginate: false }));
    dispatch(DBBotDeploymentThunks.find({ $paginate: false }));
  }, [dispatch]);
  
  // Filter for active bots (those with an activeDeploymentId)
  const activeBots = bots.filter(bot => !!bot.activeDeploymentId);

  // Add click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't handle click outside if the click was on a button that needs to trigger other actions
      if (event.target instanceof Element && event.target.closest('[data-action-button]')) {
        return;
      }
      
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);
  
  const toggleBotStatus = async (bot: BotType, event) => {
    // Prevent the click from bubbling to parent and expanding/collapsing
    event.stopPropagation();
    event.preventDefault();
    
    if (!bot?._id || togglingBots.has(bot._id)) return;
    
    const isPublished = !!bot.activeDeploymentId;
    
    // Mark bot as toggling
    setTogglingBots(prev => new Set(prev).add(bot._id));
    
    try {
      // Toggle bot activation using the same thunk from BotListContent
      await dispatch(toggleBotActivationThunk({
        bot,
        shouldActivate: !isPublished,
        findLatestDeployment: !isPublished
      }));
    } catch (error) {
      console.error('Failed to toggle bot activation:', error);
    } finally {
      // Remove bot from toggling state
      setTogglingBots(prev => {
        const next = new Set(prev);
        next.delete(bot._id);
        return next;
      });
    }
  };

  const navigateToBotManagement = (event) => {
    // Prevent the click from bubbling to parent
    event.stopPropagation();
    router.navigate('/bot-list-view');
    setIsExpanded(false);
  };
  
  const createNewBot = async (event) => {
    // Prevent the click from bubbling to parent
    event.stopPropagation();
    setIsExpanded(false);
    
    // Use the same approach as in BotListContent
    await dispatch(setIsReadyToLoadFlow(false));
    dispatch(createAndReRouteToNewBotEditorThunk({ navigate: router.navigate }));
  };
  
  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return formatTime(date);
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${formatTime(date)}`;
    } else {
      return date.toLocaleDateString() + ', ' + formatTime(date);
    }
  };
  
  const getStatusIcon = (status: boolean) => {
    if (status) {
      return <CheckCircle size={16} color="#22c55e" />;
    } else {
      return <Power size={16} color="#9ca3af" />;
    }
  };

  // Get status color based on active bots
  const getStatusColor = () => {
    if (activeBots.length > 0) {
      return '#22c55e'; // green
    } else if (bots.length > 0) {
      return '#f97316'; // orange
    } else {
      return '#9ca3af'; // gray
    }
  };

  const navigateToBotDetail = (bot: BotType) => {
    router.navigate(`/bot-detail-view/${bot._id}`);
    setIsExpanded(false);
  };

  // Render the content of the expanded view
  const renderExpandedContent = () => {
    if (isLoading) {
      return (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>Loading bots...</p>
        </div>
      );
    }
    
    if (bots.length === 0) {
      return (
        <div style={styles.emptyStateContainer}>
          <div style={styles.emptyStateIcon}>
            <Bot size={24} color="#9ca3af" />
          </div>
          <p style={styles.emptyStateText}>
            You haven't created any bots yet. Create your first bot to automate tasks and workflows.
          </p>
          <a 
            href="https://blueticks.co/guides/ai-support-agent" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#0369a1',
              fontSize: '14px',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              marginBottom: '16px',
              lineHeight: '20px',
            }}
          >
            <Info size={14} style={{ display: 'block', flexShrink: 0 }} />
            Learn how to create an AI Support Agent
          </a>
          <button 
            onClick={createNewBot}
            style={styles.createButton}
            data-action-button="true"
          >
            <Plus size={16} />
            <span>Create a Bot</span>
          </button>
        </div>
      );
    }
    
    return (
      <div>
        {bots.map(bot => {
          const isActive = !!bot.activeDeploymentId;
          const lastActive = bot.botStats?.latestDeployment?.createdAt 
            ? new Date(bot.botStats.latestDeployment.createdAt) 
            : new Date();
          const isToggling = bot._id ? togglingBots.has(bot._id) : false;
          
          return (
            <div 
              key={bot._id} 
              style={styles.botItem}
              onClick={() => navigateToBotDetail(bot)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={styles.botItemLeft}>
                {getStatusIcon(isActive)}
                <div style={styles.botInfo}>
                  <p style={styles.botName}>{bot.name || 'Unnamed Bot'}</p>
                  <div style={styles.botMeta}>
                    {bot.botStats?.latestDeployment?.version && (
                      <span style={{ marginRight: '4px' }}>Version: v{bot.botStats.latestDeployment.version}</span>
                    )}
                    {bot.botStats?.latestDeployment?.createdAt && (
                      <>
                        <span style={{ margin: '0 4px' }}>•</span>
                        <Clock size={12} color="#9ca3af" style={{ margin: '0 4px' }} />
                        <span>{formatDate(lastActive)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div 
                style={styles.toggleContainer}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isToggling) {
                    toggleBotStatus(bot, e);
                  }
                }}
                data-action-button="true"
              >
                {isToggling ? (
                  <div style={styles.spinner}></div>
                ) : (
                  <>
                    <input 
                      type="checkbox" 
                      checked={isActive} 
                      onChange={() => {}}
                      readOnly
                      style={{ display: 'none' }}
                    />
                    <div 
                      style={{
                        ...styles.toggleTrack,
                        ...(isActive ? styles.toggleTrackActive : {})
                      }}
                    >
                      <div 
                        style={{
                          ...styles.toggleThumb,
                          ...(isActive ? styles.toggleThumbActive : {})
                        }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Footer with Create Bot button */}
        <div style={styles.footerContainer}>
          <button 
            onClick={createNewBot}
            style={{...styles.footerButton, ...styles.createBotButton}}
            data-action-button="true"
          >
            <Plus size={16} />
            <span>Create New Bot</span>
          </button>
          
          <button 
            onClick={navigateToBotManagement}
            style={{...styles.footerButton, ...styles.manageBotButton}}
            data-action-button="true"
          >
            <span>Manage All Bots</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container} ref={containerRef}>
      {/* Compact Button - Always visible */}
      <button
        style={{
          ...styles.minifiedButton,
          backgroundColor: isDarkMode ? '#222e35' : 'white',
          borderColor: isDarkMode ? '#8696A0' : '#e5e7eb',
          color: isDarkMode ? '#e9edef' : 'inherit',
          boxShadow: 'none',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        data-button-id={instanceId}
        data-action-button="true"
      >
        <div
          style={{
            ...styles.statusDot,
            backgroundColor: getStatusColor()
          }}
        />
        <span style={{
          ...styles.statusLabel,
          color: isDarkMode ? '#e9edef' : 'inherit'
        }}>
          {activeBots.length > 0 
            ? `${activeBots.length} Bot${activeBots.length > 1 ? 's' : ''} Active` 
            : bots.length === 0 
              ? 'No Bots Created' 
              : 'Bots Inactive'}
        </span>
        {isExpanded ? (
          <ChevronDown size={16} color={isDarkMode ? '#8696a0' : '#6b7280'} />
        ) : (
          <ChevronUp size={16} color={isDarkMode ? '#8696a0' : '#6b7280'} />
        )}
      </button>

      {/* Expandable Dropdown using shared component */}
      {isExpanded && (
        <ExpandedView
          styles={{
            ...styles,
            expandedView: {
              ...styles.expandedView,
              backgroundColor: isDarkMode ? '#2A3942' : 'white',
              borderColor: isDarkMode ? '#8696A0' : '#e5e7eb',
            }
          }}
          onClose={() => setIsExpanded(false)}
          buttonId={instanceId}
          title="Bot Management"
        >
          {renderExpandedContent()}
        </ExpandedView>
      )}
    </div>
  );
};

export default BotStatusComponent; 